/**
 * Developmental Age Computation Utilities
 * 
 * This module provides probabilistic developmental age scoring using PAS (Professional Assessment System) data.
 * It implements sophisticated interpolation and extrapolation algorithms to compute developmental ages,
 * delays, and assessment results based on milestone answers and normative data.
 */

import { mapDomainToAdminNodeName } from './devFloorCeiling';
import { 
  AdminNodeDto,
  AdminNodeParamsDto,
  AdminNodeMeanDto,
  AdminNodeProbDto,
  AdminNodeCutsDto,
  AdminNodeOrderDto,
  AdminSegmentDto,
  AdminHtmlAnswerDto
} from '../types/adminSegment';

// Answer set configuration for milestone processing
export interface MilestoneAnswerSet {
  languageTypeId: string;
  answers: {
    dataValue: number;
    textLabel: string;
    keyValue: number;
  }[];
}

// Type definitions for developmental age computation
export interface DevelopmentalAgeInput {
  domainNode: AdminNodeDto;           // Domain-specific PAS node with Probs, Means, Params, Cuts
  domainKey?: string;                 // Domain key for result variable naming (e.g., 'CDC_LC', 'CDC_SE')
  childPhysicalAge: number;           // Child's chronological age in months
  milestoneAnswers: Map<string, number>; // Map of MVarName -> DataValue (1="Yes", 2="Sometimes", 3="Not Yet")
  answerSet: MilestoneAnswerSet;      // Answer set configuration for this language
  devFloorOrderIndex?: number | null; // 1-based index into AdminNodeDto.Order for the floor milestone
  devCeilingOrderIndex?: number | null; // 1-based index into AdminNodeDto.Order for the ceiling milestone
  debugMode?: boolean;                // Enable detailed logging
}

export interface DevelopmentalAgeResult {
  developmentalAge: number;           // Computed developmental age in months
  delay: number;                      // Delay amount (physical age - developmental age)
  result: 'typical' | 'concern' | 'significant-delay'; // Assessment result category
  resultScore: number;               // Numeric score for the result
  isCappedAtDomainMax?: boolean;     // True when dev window is capped at domain end and actual result is unknown
  isCappedAtDomainMin?: boolean;     // True when dev window is capped at domain start and actual result is unknown
  itemsScored: number;               // Number of milestones that contributed to score
  totalItems: number;                // Total number of milestones in domain
  rawScore: number;                  // Raw score before age conversion
  domainResults?: DomainSpecificResults; // Domain-specific result variables
  debugInfo?: DevelopmentalAgeDebugInfo; // Detailed computation information
}

// Domain-specific result variables for script logic and data storage
export interface DomainSpecificResults {
  domainKey: string;                 // Domain identifier (e.g., 'CDC_LC', 'CDC_SE')
  scoreVarName: string;              // MVarName for SCORE variable
  delayVarName: string;              // MVarName for DELAY variable  
  resultVarName: string;             // MVarName for RESULT variable
  resultScoreVarName: string;        // MVarName for RESULTSCORE variable
  scoreValue: number;                // SCORE = developmentalAge
  delayValue: number;                // DELAY = developmentalAge - childPhysicalAge
  resultValue?: string;              // RESULT = (to be computed)
  resultScoreValue?: number;         // RESULTSCORE = (to be computed)
}

export interface DevelopmentalAgeDebugInfo {
  interpolatedProbs: Array<{
    mvar: string;
    answerDataValue: number;
    answerLabel?: string;
    interpolatedProb: number;
    ageUsed: number;
  }>;
  cutScoreDetails: {
    delayThresholds: Array<{ delay: number, cutScore: number }>;
    interpolatedCutScore: number;
    resultCategory: string;
  };
  computationSteps: string[];
}

// Probability interpolation configuration
export interface ProbabilityInterpolationOptions {
  method: 'linear' | 'cubic' | 'logarithmic'; // Interpolation method
  extrapolationMode: 'extend' | 'clamp' | 'linear-extend'; // How to handle out-of-bounds ages
  minAge: number;                     // Minimum age for extrapolation
  maxAge: number;                     // Maximum age for extrapolation
  minProbValue?: number;              // Minimum returned probability floor to avoid zero-collapse
}

// Cut score interpolation configuration
export interface CutScoreInterpolationOptions {
  method: 'linear' | 'step' | 'smooth'; // Interpolation method for cut scores
  thresholdSmoothing: boolean;        // Apply smoothing to threshold boundaries
}

// A priori probability options for Bayesian developmental age computation
export interface APrioriOptions {
  pass1MaxBoost: number;       // Maximum Gaussian boost at center of Pass 1 (1-month resolution)
  pass1StdDevFactor: number;   // Fraction of window range for Pass 1 std dev: stdDev = factor × (probWindowMax - probWindowMin)
  pass2MaxBoost: number;       // Maximum Gaussian boost at center of Pass 2 (0.1-month precision)
  pass2StdDev: number;         // Standard deviation in months for Pass 2 normal distribution
}

/**
 * Extract milestone answer set from AdminSegment questions
 * All milestones use the same answer set, so we only need to load it once per language
 */
export function extractAnswerSet(
  adminSegment: AdminSegmentDto, 
  languageTypeId: string
): MilestoneAnswerSet {
  
  // Find the first question with HtmlQuestion data in the specified language
  for (const page of adminSegment.Pages) {
    for (const questionList of page.Questions) {
      if (questionList.Question?.HtmlQuestion) {
        for (const htmlQuestion of questionList.Question.HtmlQuestion) {
          if (htmlQuestion.languageTypeId === languageTypeId && htmlQuestion.htmlAnswer?.length > 0) {
            
            // Extract answer data values from the first available question
            const answers = htmlQuestion.htmlAnswer.map(answer => ({
              dataValue: parseInt(answer.dataValue, 10),
              textLabel: answer.aText,
              keyValue: parseInt(answer.keyValue, 10)
            })).filter(answer => !isNaN(answer.dataValue) && !isNaN(answer.keyValue));
            
            // Sort by keyValue to maintain proper display order
            answers.sort((a, b) => a.keyValue - b.keyValue);
            
            return {
              languageTypeId,
              answers
            };
          }
        }
      }
    }
  }
  
  // Fallback to default English answer set if not found
  console.warn(`⚠️ Could not find answer set for language ${languageTypeId}, using default English values`);
  return {
    languageTypeId: 'en',
    answers: [
      { dataValue: 1, textLabel: 'Yes', keyValue: 1 },
      { dataValue: 2, textLabel: 'Sometimes', keyValue: 2 },
      { dataValue: 3, textLabel: 'Not Yet', keyValue: 3 }
    ]
  };
}

/**
 * Extract milestone data from AdminSegment questions  
 * Returns Map of MVarName -> question data for milestone processing
 */
export function extractMilestoneQuestions(
  adminSegment: AdminSegmentDto,
  languageTypeId: string
): Map<string, { mvarName: string; questionText: string }> {
  
  const milestoneQuestions = new Map<string, { mvarName: string; questionText: string }>();
  
  for (const page of adminSegment.Pages) {
    for (const questionList of page.Questions) {
      const question = questionList.Question;
      if (question?.MvarName) {
        
        // Find the question text for the specified language
        let questionText = `Question ${question.MvarName}`; // Fallback
        
        if (question.HtmlQuestion) {
          const htmlQuestion = question.HtmlQuestion.find(
            hq => hq.languageTypeId === languageTypeId
          );
          if (htmlQuestion?.qText) {
            questionText = htmlQuestion.qText;
          }
        }
        
        milestoneQuestions.set(question.MvarName, {
          mvarName: question.MvarName,
          questionText
        });
      }
    }
  }
  
  return milestoneQuestions;
}

/**
 * Map domain keys to standardized result variable names for script logic
 */
export function getDomainResultVariableNames(domainKey: string): {
  scoreVarName: string;
  delayVarName: string;
  resultVarName: string;
  resultScoreVarName: string;
} {
  
  // Normalize domain key to base domain for variable naming
  const baseDomain = normalizeDomainKey(domainKey);
  
  return {
    scoreVarName: `${baseDomain}_SCORE`,
    delayVarName: `${baseDomain}_DELAY`, 
    resultVarName: `${baseDomain}_RESULT`,
    resultScoreVarName: `${baseDomain}_RESULTSCORE`
  };
}

/**
 * Normalize domain key to base domain for consistent variable naming
 */
function normalizeDomainKey(domainKey: string): string {
  // Handle domain variations (CDC_LC and CDCX_LC both map to CDC_LC)
  const domain = domainKey.toUpperCase();
  
  // Handle MilestonesView domain keys
  if (domain === 'COMMUNICATION') return 'CDC_LC';      // Language & Communication
  if (domain === 'SOCIALEMOTIONAL') return 'CDC_SE';    // Social Emotional  
  if (domain === 'COGNITIVE') return 'CDC_CG';          // Cognitive
  if (domain === 'MOTOR') return 'CDC_MT';              // Motor (general)
  if (domain === 'FINEMOTOR') return 'CDC_FM';          // Fine Motor
  if (domain === 'GROSSMOTOR') return 'CDC_GM';         // Gross Motor
  
  // Handle PAS domain codes
  if (domain.includes('LC')) return 'CDC_LC';           // Language & Communication
  if (domain.includes('SE')) return 'CDC_SE';           // Social Emotional  
  if (domain.includes('CG')) return 'CDC_CG';           // Cognitive
  if (domain.includes('MT')) return 'CDC_MT';           // Motor (general)
  if (domain.includes('FM')) return 'CDC_FM';           // Fine Motor
  if (domain.includes('GM')) return 'CDC_GM';           // Gross Motor
  
  // Default fallback - use the original domain key
  console.warn(`⚠️ Unknown domain key ${domainKey}, using as-is for variable naming`);
  return domain;
}

/**
 * Calculate domain-specific RESULT based on delay and cut score thresholds
 */
async function calculateDomainResult(
  cuts: AdminNodeCutsDto[],
  physicalAge: number,
  delay: number,
  options: CutScoreInterpolationOptions
): Promise<{ resultValue: string, resultScoreValue: number, cutValue: number }> {
  
  console.log(`🎯 Calculating domain RESULT for age ${physicalAge}mo, delay ${delay}mo`);
  
  if (!cuts || cuts.length === 0) {
    console.warn('⚠️ No cut score data available for domain RESULT, using default');
    // Default: 1 STD = 3 months delay
    const cutValue = 3.0;
    return calculateResultFromCutValue(delay, cutValue);
  }
  
  // Get the cut value (1 standard deviation) for this age
  const cutValue = await interpolateCutValueForAge(cuts, physicalAge, options);
  console.log(`  - Cut value (1 STD) for age ${physicalAge}mo: ${cutValue.toFixed(2)}mo`);
  
  return calculateResultFromCutValue(delay, cutValue);
}

/**
 * Interpolate cut value (1 STD) for specific age from Cuts array
 */
async function interpolateCutValueForAge(
  cuts: AdminNodeCutsDto[],
  physicalAge: number,
  options: CutScoreInterpolationOptions
): Promise<number> {
  
  // Parse cut score data to extract age-specific cut values
  const cutPoints: Array<{ age: number, cutValue: number }> = [];
  
  for (const cutEntry of cuts) {
    // Parse age from cut entry RVal (could be single age like "24" or range like "18-30")
    const ages = parseAgeFromRVal(cutEntry.RVal);
    if (ages.length > 0) {
      // Use the first age from the parsed ages (for ranges, parseAgeFromRVal returns midpoint)
      const age = ages[0];
      const cutValue = parseFloat(cutEntry.CVal);
      if (!isNaN(cutValue)) {
        cutPoints.push({ age, cutValue });
      }
    }
  }
  
  if (cutPoints.length === 0) {
    console.warn('⚠️ Could not parse cut score data, using default 1 STD = 3.0mo');
    return 3.0;
  }
  
  // Sort by age for interpolation
  cutPoints.sort((a, b) => a.age - b.age);
  
  // Handle exact age matches
  const exactMatch = cutPoints.find(p => Math.abs(p.age - physicalAge) < 0.001);
  if (exactMatch) {
    return exactMatch.cutValue;
  }
  
  // Interpolate between adjacent ages
  if (physicalAge <= cutPoints[0].age) {
    return cutPoints[0].cutValue; // Use first available
  }
  
  if (physicalAge >= cutPoints[cutPoints.length - 1].age) {
    return cutPoints[cutPoints.length - 1].cutValue; // Use last available
  }
  
  // Linear interpolation between two points
  for (let i = 0; i < cutPoints.length - 1; i++) {
    const p1 = cutPoints[i];
    const p2 = cutPoints[i + 1];
    
    if (physicalAge >= p1.age && physicalAge <= p2.age) {
      const ratio = (physicalAge - p1.age) / (p2.age - p1.age);
      return p1.cutValue + ratio * (p2.cutValue - p1.cutValue);
    }
  }
  
  return cutPoints[0].cutValue; // Fallback
}

/**
 * Calculate RESULT and RESULTSCORE from delay and cut value
 */
function calculateResultFromCutValue(delay: number, cutValue: number): { 
  resultValue: string, 
  resultScoreValue: number, 
  cutValue: number 
} {
  
  // Use absolute delay for comparison (delays are typically negative)
  const oneSTD = cutValue;
  const twoSTD = cutValue * 2;
  
  let resultValue: string;
  let resultIndex: number; // 0, 1, or 2 for RESULT
  
  if (delay <= -twoSTD) {
    // Less than 1 STD delay
    resultIndex = 0;
    resultValue = "Below Cutoff";
  } else if (delay < -oneSTD) {
    // Between 1 and 2 STDs delay  
    resultIndex = 1;
    resultValue = "Close to Cutoff";
  } else {
    // 2 STDs or greater delay
    resultIndex = 2; 
    resultValue = "Above Cutoff";
  }
  
  // Calculate RESULTSCORE using normalized linear scale (0-100)
  // Formula: RESULTSCORE = (1 + (DELAY/(2*STD)))*100
  let resultScoreValue: number;
  
  if (delay <= -twoSTD) {
    // Cap at 0 for DELAY <= -2*STD
    resultScoreValue = 0;
  } else if (delay >= 0) {
    // Cap at 100 for DELAY >= 0
    resultScoreValue = 100;
  } else {
    // Linear interpolation: RESULTSCORE = (1 + (DELAY/(2*STD)))*100
    resultScoreValue = (1 + (delay / (2 * cutValue))) * 100;
    
    // Ensure bounds (should be between 0 and 100 by formula)
    resultScoreValue = Math.max(0, Math.min(100, resultScoreValue));
  }
  
  console.log(`  - Delay: ${delay.toFixed(2)}mo, 1STD: ${oneSTD.toFixed(2)}mo, 2STD: ${twoSTD.toFixed(2)}mo`);
  console.log(`  - RESULT: ${resultIndex} (${resultValue})`);
  console.log(`  - RESULTSCORE: ${resultScoreValue.toFixed(1)}`);
  
  return { resultValue, resultScoreValue, cutValue };
}

/**
 * Parse age from cut score RVal string
 */
function parseAgeFromCutRVal(rVal: string): number | null {
  if (!rVal) return null;
  
  // Try various age parsing patterns
  const patterns = [
    /age[_\s]*(\d+)/i,    // "age_24", "age 24"
    /(\d+)[_\s]*mo/i,     // "24_mo", "24 mo"  
    /(\d+)[_\s]*months?/i, // "24_month", "24 months"
    /^(\d+)$/             // Just "24"
  ];
  
  for (const pattern of patterns) {
    const match = rVal.match(pattern);
    if (match) {
      const age = parseInt(match[1], 10);
      if (!isNaN(age) && age > 0 && age <= 72) { // Reasonable age range
        return age;
      }
    }
  }
  
  return null;
}

/**
 * Compute domain-specific result variables
 */
async function computeDomainResults(
  domainKey: string,
  developmentalAge: number,
  childPhysicalAge: number,
  cuts: AdminNodeCutsDto[],
  options: CutScoreInterpolationOptions
): Promise<DomainSpecificResults> {
  
  const variableNames = getDomainResultVariableNames(domainKey);
  const baseDomain = normalizeDomainKey(domainKey);
  
  // Calculate core values
  const scoreValue = developmentalAge;                           // SCORE = developmentalAge  
  const delayValue = developmentalAge - childPhysicalAge;        // DELAY = developmentalAge - childPhysicalAge
  
  // Calculate RESULT and RESULTSCORE using cut score analysis
  const resultCalculation = await calculateDomainResult(cuts, childPhysicalAge, delayValue, options);
  
  return {
    domainKey: baseDomain,
    scoreVarName: variableNames.scoreVarName,
    delayVarName: variableNames.delayVarName,
    resultVarName: variableNames.resultVarName,
    resultScoreVarName: variableNames.resultScoreVarName,
    scoreValue,
    delayValue,
    resultValue: resultCalculation.resultValue,
    resultScoreValue: resultCalculation.resultScoreValue
  };
}

/**
 * Main developmental age computation function
 * 
 * This function implements the core PAS scoring algorithm to compute developmental age
 * based on milestone responses and normative probability data.
 */
export async function computeDevelopmentalAge(
  input: DevelopmentalAgeInput,
  probOptions: ProbabilityInterpolationOptions = getDefaultProbabilityOptions(),
  cutOptions: CutScoreInterpolationOptions = getDefaultCutScoreOptions(),
  aPrioriOptions: APrioriOptions = getDefaultAPrioriOptions()
): Promise<DevelopmentalAgeResult> {
  
  const startTime = performance.now();
  const debug = input.debugMode || false;
  const computationSteps: string[] = [];
  
  console.log(`🧮 computeDevelopmentalAge called with:`, {
    domainName: input.domainNode?.Name,
    childAge: input.childPhysicalAge,
    answersProvided: input.milestoneAnswers?.size || 0,
    debugMode: debug,
    hasProbs: !!input.domainNode?.Probs,
    hasMeans: !!input.domainNode?.Means,
    hasCuts: !!input.domainNode?.Cuts,
    hasOrder: !!input.domainNode?.Order
  });
  
  // Step 1: Validate input data
  const validationResult = validateInputData(input);
  if (!validationResult.valid) {
    console.error('❌ Input validation failed:', validationResult.errors);
    throw new Error(`Invalid input data: ${validationResult.errors.join(', ')}`);
  }
  console.log('✅ Input data validation passed');

  // Step 1.5: Filter milestone answers to only include those from this domain
  const domainMvars = new Set<string>();
  if (input.domainNode.Order) {
    for (const orderItem of input.domainNode.Order) {
      if (orderItem.Name) {
        domainMvars.add(orderItem.Name);
      }
    }
  }
  
  console.log(`📋 Domain Order contains ${domainMvars.size} milestone variables`);
  if (domainMvars.size > 0) {
    console.log(`📝 Sample domain mvars:`, Array.from(domainMvars).slice(0, 5));
  }
  
  // Create filtered milestone answers map containing only domain-specific milestones
  const filteredMilestoneAnswers = new Map<string, number>();
  for (const [mvar, answerDataValue] of input.milestoneAnswers) {
    if (domainMvars.has(mvar)) {
      filteredMilestoneAnswers.set(mvar, answerDataValue);
    }
  }
  
  console.log(`🔍 Filtered milestone answers: ${filteredMilestoneAnswers.size} domain-specific (${input.milestoneAnswers.size} total provided)`);
  if (filteredMilestoneAnswers.size > 0) {
    console.log(`📝 Sample filtered answers:`, Array.from(filteredMilestoneAnswers.entries()).slice(0, 3));
  }
  
  if (debug) {
    console.log('🧮 Starting developmental age computation...');
    console.log(`  - Domain: ${input.domainNode.Name}`);
    console.log(`  - Child age: ${input.childPhysicalAge} months`);
    console.log(`  - Milestone answers: ${filteredMilestoneAnswers.size} domain-specific responses (${input.milestoneAnswers.size} total provided)`);
    console.log(`  - Domain-specific milestones in Order: ${domainMvars.size}`);
    if (filteredMilestoneAnswers.size === 0) {
      console.warn('⚠️ No milestone answers found for this domain after filtering');
    }
    computationSteps.push(`Starting computation for domain ${input.domainNode.Name}, child age ${input.childPhysicalAge}mo`);
  }

  // Step 2: Extract and prepare PAS data
  const pasData = extractPasData(input.domainNode);
  if (debug) {
    console.log(`  - Extracted PAS data: ${pasData.probs.length} probability entries, ${pasData.cuts.length} cut scores`);
    computationSteps.push(`Extracted ${pasData.probs.length} probability entries and ${pasData.cuts.length} cut scores`);
  }

  // Step 3: Initialize probability array based on filtered domain data
  const mvarnames = Array.from(filteredMilestoneAnswers.keys());
  
  // Create sorted milestone data with mean ages and answer values
  const milestoneData: Array<{ mvar: string, meanAge: number, answerDataValue: number, answerLabel: string }> = [];
  let minItemAge = Number.MAX_VALUE;
  let maxItemAge = Number.MIN_VALUE;

  // Collect milestone data with their mean ages
  console.log(`🔍 Processing ${mvarnames.length} milestone variables for age calculation`);
  
  for (const mvar of mvarnames) {
    const meanEntry = pasData.means.find(mean => mean.Name === mvar);
    const answerDataValue = filteredMilestoneAnswers.get(mvar);
    
    if (meanEntry && answerDataValue !== undefined) {
      const itemAge = parseFloat(meanEntry.Mean);
      if (!isNaN(itemAge)) {
        const answerLabel = input.answerSet.answers.find(a => a.dataValue === answerDataValue)?.textLabel || answerDataValue.toString();
        
        milestoneData.push({
          mvar,
          meanAge: itemAge,
          answerDataValue,
          answerLabel
        });
        
        minItemAge = Math.min(minItemAge, itemAge);
        maxItemAge = Math.max(maxItemAge, itemAge);
      } else {
        console.warn(`⚠️ Invalid mean age for ${mvar}: ${meanEntry.Mean}`);
      }
    } else {
      if (!meanEntry) {
        console.warn(`⚠️ No mean entry found for ${mvar}`);
      }
      if (answerDataValue === undefined) {
        console.warn(`⚠️ No answer data value found for ${mvar}`);
      }
    }
  }

  console.log(`📊 Collected ${milestoneData.length} valid milestone data points`);
  if (milestoneData.length > 0) {
    console.log(`🎯 Age range: ${minItemAge}mo - ${maxItemAge}mo`);
    console.log(`📝 Sample milestone data:`, milestoneData.slice(0, 3).map(m => ({
      mvar: m.mvar,
      meanAge: m.meanAge,
      answer: m.answerLabel
    })));
  }

  // Handle case where no valid item ages were found
  if (milestoneData.length === 0) {
    console.error('❌ No valid milestone data found - this explains wrong developmental age!');
    throw new Error('No valid milestone data found in PAS Means');
  }
  
  // Sort milestones chronologically by mean age
  milestoneData.sort((a, b) => a.meanAge - b.meanAge);
  
  // Find minValidAge: first milestone from earliest age with dataValue > 1
  let minValidAge = minItemAge; // Default fallback
  for (let i = 0; i < milestoneData.length; i++) {
    if (milestoneData[i].answerDataValue > 1) {
      minValidAge = milestoneData[i].meanAge;
      break;
    }
  }
  
  // Find maxValidAge: first milestone from latest age with dataValue < 3
  let maxValidAge = maxItemAge; // Default fallback
  for (let i = milestoneData.length - 1; i >= 0; i--) {
    if (milestoneData[i].answerDataValue < 3) {
      maxValidAge = milestoneData[i].meanAge;
      break;
    }
  }
  
  if (debug) {
    console.log(`  - Chronologically sorted milestone data:`);
    for (const item of milestoneData) {
      console.log(`    - ${item.mvar}: age=${item.meanAge}mo, answer=${item.answerDataValue} ("${item.answerLabel}")`);
    }
    console.log(`  - minValidAge: ${minValidAge}mo (first milestone with dataValue > 1)`);
    console.log(`  - maxValidAge: ${maxValidAge}mo (last milestone with dataValue < 3)`);
  }

  // Calculate probability window bounds — use devFloor/devCeiling item ages when available
  let windowBaseMin = minItemAge;
  let windowBaseMax = maxItemAge;

  if (input.devFloorOrderIndex != null && input.devCeilingOrderIndex != null && input.domainNode.Order?.length) {
    const floorEntry = input.domainNode.Order[input.devFloorOrderIndex - 1];
    const ceilingEntry = input.domainNode.Order[input.devCeilingOrderIndex - 1];
    if (floorEntry && ceilingEntry) {
      const floorItemAge = parseFloat(floorEntry.Val);
      const ceilingItemAge = parseFloat(ceilingEntry.Val);
      if (!isNaN(floorItemAge) && !isNaN(ceilingItemAge)) {
        windowBaseMin = floorItemAge;
        windowBaseMax = ceilingItemAge;
        if (debug) {
          console.log(`  - Using devFloor/devCeiling window: [${floorItemAge}mo, ${ceilingItemAge}mo] (order indices floor=${input.devFloorOrderIndex}, ceiling=${input.devCeilingOrderIndex})`);
        }
      }
    }
  }

  // Build window-filtered evidence set: only milestones whose mean age falls within [windowBaseMin, windowBaseMax]
  // This prevents "Yes" answers below the floor and "Not Yet" answers above the ceiling from flooding the computation
  const windowFilteredAnswers = new Map<string, number>();
  for (const item of milestoneData) {
    if (item.meanAge >= windowBaseMin && item.meanAge <= windowBaseMax) {
      windowFilteredAnswers.set(item.mvar, item.answerDataValue);
    }
  }

  if (debug) {
    console.log(`  - Window-filtered evidence: ${windowFilteredAnswers.size} of ${milestoneData.length} domain answers within [${windowBaseMin.toFixed(1)}mo, ${windowBaseMax.toFixed(1)}mo]`);
  }

  // Special handling: if the dev window is capped at the end of the domain list and
  // window answers have no blanks or "Not Yet", with <=2 "Sometimes", treat age as capped.
  const domainOrder = input.domainNode.Order ?? [];
  const ceilingAtDomainEnd =
    input.devCeilingOrderIndex != null &&
    domainOrder.length > 0 &&
    input.devCeilingOrderIndex >= domainOrder.length;

  const floorAtDomainStart =
    input.devFloorOrderIndex != null &&
    domainOrder.length > 0 &&
    input.devFloorOrderIndex <= 1;

  if (floorAtDomainStart && domainOrder.length > 0) {
    const ceilingIndex = Math.max(1, Math.min(domainOrder.length, input.devCeilingOrderIndex ?? domainOrder.length));
    const cappedWindowItems = domainOrder.slice(0, ceilingIndex);

    let blankCount = 0;
    let yesCount = 0;
    let sometimesCount = 0;

    for (const item of cappedWindowItems) {
      const answer = input.milestoneAnswers.get(item.Name);
      if (answer === undefined) {
        blankCount++;
      } else if (answer === 1) {
        yesCount++;
      } else if (answer === 2) {
        sometimesCount++;
      }
    }

    const shouldCapAtDomainStart =
      blankCount === 0 &&
      yesCount === 0 &&
      sometimesCount <= 2;

    if (shouldCapAtDomainStart) {
      const firstItem = domainOrder[0];
      const firstItemAge = parseFloat(firstItem.Val);

      if (!isNaN(firstItemAge)) {
        if (debug) {
          console.log(
            `  - Applying capped-at-start rule: age=${firstItemAge}mo (blank=${blankCount}, yes=${yesCount}, sometimes=${sometimesCount})`
          );
          computationSteps.push(
            `Capped at domain min age ${firstItemAge}mo (window start reached, no blank/yes, sometimes<=2)`
          );
        }

        return {
          developmentalAge: parseFloat(firstItemAge.toFixed(1)),
          delay: parseFloat((firstItemAge - input.childPhysicalAge).toFixed(1)),
          result: 'typical',
          resultScore: 0,
          isCappedAtDomainMax: false,
          isCappedAtDomainMin: true,
          itemsScored: cappedWindowItems.length,
          totalItems: filteredMilestoneAnswers.size,
          rawScore: 0,
          debugInfo: debug
            ? {
                interpolatedProbs: [],
                cutScoreDetails: {
                  delayThresholds: [],
                  interpolatedCutScore: 0,
                  resultCategory: 'Capped'
                },
                computationSteps
              }
            : undefined
        };
      }
    }
  }

  if (ceilingAtDomainEnd && domainOrder.length > 0) {
    const floorIndex = Math.max(0, (input.devFloorOrderIndex ?? 1) - 1);
    const cappedWindowItems = domainOrder.slice(floorIndex);

    let blankCount = 0;
    let notYetCount = 0;
    let sometimesCount = 0;

    for (const item of cappedWindowItems) {
      const answer = input.milestoneAnswers.get(item.Name);
      if (answer === undefined) {
        blankCount++;
      } else if (answer === 3) {
        notYetCount++;
      } else if (answer === 2) {
        sometimesCount++;
      }
    }

    const shouldCapAtDomainEnd =
      blankCount === 0 &&
      notYetCount === 0 &&
      sometimesCount <= 2;

    if (shouldCapAtDomainEnd) {
      const lastItem = domainOrder[domainOrder.length - 1];
      const lastItemAge = parseFloat(lastItem.Val);

      if (!isNaN(lastItemAge)) {
        if (debug) {
          console.log(
            `  - Applying capped-at-end rule: age=${lastItemAge}mo (blank=${blankCount}, notYet=${notYetCount}, sometimes=${sometimesCount})`
          );
          computationSteps.push(
            `Capped at domain max age ${lastItemAge}mo (window end reached, no blank/not-yet, sometimes<=2)`
          );
        }

        return {
          developmentalAge: parseFloat(lastItemAge.toFixed(1)),
          delay: parseFloat((lastItemAge - input.childPhysicalAge).toFixed(1)),
          result: 'typical',
          resultScore: 0,
          isCappedAtDomainMax: true,
          isCappedAtDomainMin: false,
          itemsScored: cappedWindowItems.length,
          totalItems: filteredMilestoneAnswers.size,
          rawScore: 0,
          debugInfo: debug
            ? {
                interpolatedProbs: [],
                cutScoreDetails: {
                  delayThresholds: [],
                  interpolatedCutScore: 0,
                  resultCategory: 'Capped'
                },
                computationSteps
              }
            : undefined
        };
      }
    }
  }

  const allowZeroStart = minItemAge < 1 || windowBaseMin < 1;
  const minWindowAge = allowZeroStart ? 0 : 1;
  const probWindowMin = Math.max(minWindowAge, Math.floor(windowBaseMin) - 1); // Round down, subtract 1, allow 0.0mo when early milestones exist
  const probWindowMax = Math.min(60, Math.ceil(windowBaseMax) + 1); // Round up, add 1, limit to <= 60
  const probDelta = 1.0; // Probability array resolution in months (1.0 = each element represents 1 month)
  const windowCount = Math.ceil((probWindowMax - probWindowMin) / probDelta) + 1;

  // Create probability array with equal probability (64-bit floating point)
  const probabilityArray = new Float64Array(windowCount);
  const equalProbability = 1.0 / windowCount;
  probabilityArray.fill(equalProbability);

  // Apply a priori: normal distribution centered at the midpoint of the probability window.
  // stdDev = pass1StdDevFactor × window range in months (e.g. 0.4 × 6mo = 2.4mo).
  const centerAge = (probWindowMin + probWindowMax) / 2;
  const centerIndex = (centerAge - probWindowMin) / probDelta;
  const standardDeviation = aPrioriOptions.pass1StdDevFactor * (probWindowMax - probWindowMin);
  const maxBoost = aPrioriOptions.pass1MaxBoost;

  if (debug) {
    console.log(`  - Applying a priori (Pass 1): center=${centerAge}mo (index ${centerIndex.toFixed(1)}), stdDev=${standardDeviation.toFixed(2)}mo, maxBoost=${maxBoost}`);
  }

  for (let i = 0; i < probabilityArray.length; i++) {
    const distanceFromCenter = i - centerIndex;
    const normalizedDistance = distanceFromCenter / standardDeviation;
    const gaussianValue = Math.exp(-0.5 * normalizedDistance * normalizedDistance);
    const boostFactor = 1 + (maxBoost - 1) * gaussianValue;
    probabilityArray[i] *= boostFactor;

    if (debug) {
      const age = probWindowMin + (i * probDelta);
      console.log(`    - Age ${age}mo (index ${i}): gaussian=${gaussianValue.toFixed(4)}, boost=${boostFactor.toFixed(3)} → ${probabilityArray[i].toFixed(8)}`);
    }
  }

  if (debug) {
    console.log(`  - Item age range: ${minItemAge.toFixed(2)} to ${maxItemAge.toFixed(2)} months`);
    console.log(`  - Probability window: ${probWindowMin} to ${probWindowMax} months (${windowCount} elements)`);
    console.log(`  - Probability resolution: ${probDelta}mo per array element`);
    console.log(`  - Initial probability per age: ${equalProbability.toFixed(6)}`);
    computationSteps.push(`Initialized probability array: window [${probWindowMin}-${probWindowMax}] with ${windowCount} elements at ${probDelta}mo resolution`);
  }

  // Step 4: Apply probability evidence for each answered milestone (window-filtered only)
  const probScores: Array<{ mvar: string, answer: number, probScore: number, age: number }> = [];
  let itemsScored = 0;

  for (const [mvar, answerDataValue] of windowFilteredAnswers) {
    if (debug) {
      const answerLabel = input.answerSet.answers.find(a => a.dataValue === answerDataValue)?.textLabel || answerDataValue.toString();
      console.log(`  - Processing milestone: ${mvar} (dataValue=${answerDataValue}, "${answerLabel}")`);
    }

    let milestoneContributed = false;

    // Iterate through each position in the probability array
    for (let i = 0; i < probabilityArray.length; i++) {
      const currentAge = probWindowMin + (i * probDelta);
      
      // Look up probability value for this milestone answer at this age
      const probValue = await interpolateProbabilityValue(
        pasData.probs,
        mvar,
        answerDataValue,
        currentAge,
        probOptions
      );

      if (probValue !== null) {
        // Store the original value before Bayesian update for debugging
        const originalValue = probabilityArray[i];
        
        // Multiply current array value by the probability (Bayesian update)
        probabilityArray[i] *= probValue;
        milestoneContributed = true;
        
        if (debug && probValue !== 1.0) {
          console.log(`    - Age ${currentAge}mo: prob = ${probValue.toFixed(6)}, original = ${originalValue.toFixed(8)} → array[${i}] = ${probabilityArray[i].toFixed(8)}`);
        }
      }
    }

    if (milestoneContributed) {
      // Normalize the probability array to sum=100 after each Bayesian update.
      // This keeps values in a consistent numeric range and prevents floating-point
      // underflow when many milestones are applied in sequence.
      let arraySum = 0;
      for (let i = 0; i < probabilityArray.length; i++) {
        arraySum += probabilityArray[i];
      }

      if (arraySum > 0) {
        const normFactor = 100 / arraySum;
        for (let i = 0; i < probabilityArray.length; i++) {
          probabilityArray[i] *= normFactor;
        }

        if (debug) {
          console.log(`    - Normalized array to sum=100 after applying ${mvar}`);
        }
      } else {
        console.warn(`⚠️ Probability array sum is zero after applying ${mvar}, skipping normalization`);
      }

      itemsScored++;
      probScores.push({ mvar, answer: answerDataValue, probScore: 0, age: 0 }); // Placeholder for compatibility
    }
  }

  if (itemsScored === 0) {
    throw new Error('No valid milestone responses found for scoring');
  }

  // Find the most probable developmental age (array element with largest value)
  let maxProbIndex = 0;
  let maxProbValue = probabilityArray[0];
  for (let i = 1; i < probabilityArray.length; i++) {
    if (probabilityArray[i] > maxProbValue) {
      maxProbValue = probabilityArray[i];
      maxProbIndex = i;
    }
  }

  const initialDevelopmentalAge = probWindowMin + (maxProbIndex * probDelta);

  
  // Step 4a: Precision refinement using high-resolution probability window
  if (debug) {
    console.log(`\n🔍 Step 4a: Refining developmental age around ${initialDevelopmentalAge}mo with high precision...`);
  }
  
  // Create narrow, high-precision window around initial result
  const precisionWindowRange = 0.5; // ±5 elements at 0.1mo resolution = 11 elements total
  const precisionDelta = 0.1; // High precision: 0.1 month resolution
  const precisionWindowMin = Math.max(minWindowAge, initialDevelopmentalAge - precisionWindowRange);
  const precisionWindowMax = Math.min(60, initialDevelopmentalAge + precisionWindowRange);
  const precisionWindowCount = Math.ceil((precisionWindowMax - precisionWindowMin) / precisionDelta) + 1;
  
  // Create high-precision probability array
  const precisionProbabilityArray = new Float64Array(precisionWindowCount);
  const precisionEqualProbability = 1.0 / precisionWindowCount;
  precisionProbabilityArray.fill(precisionEqualProbability);
  
  // Apply a priori Gaussian boost centered on initialDevelopmentalAge
  const pass2CenterIndex = (initialDevelopmentalAge - precisionWindowMin) / precisionDelta;
  const pass2StdDevIndex = aPrioriOptions.pass2StdDev / precisionDelta;

  if (debug) {
    console.log(`  - Applying normal distribution a priori (Pass 2): center=${initialDevelopmentalAge}mo, stdDev=${aPrioriOptions.pass2StdDev}mo, maxBoost=${aPrioriOptions.pass2MaxBoost}`);
  }

  for (let i = 0; i < precisionProbabilityArray.length; i++) {
    const distanceFromCenter = i - pass2CenterIndex;
    const normalizedDistance = distanceFromCenter / pass2StdDevIndex;
    const gaussianValue = Math.exp(-0.5 * normalizedDistance * normalizedDistance);
    const boostFactor = 1 + (aPrioriOptions.pass2MaxBoost - 1) * gaussianValue;
    precisionProbabilityArray[i] *= boostFactor;

    if (debug) {
      const age = precisionWindowMin + (i * precisionDelta);
      console.log(`    - Age ${age.toFixed(1)}mo (index ${i}): gaussian=${gaussianValue.toFixed(4)}, boost=${boostFactor.toFixed(3)} → ${precisionProbabilityArray[i].toFixed(8)}`);
    }
  }

  if (debug) {
    console.log(`  - Precision window: ${precisionWindowMin.toFixed(1)} to ${precisionWindowMax.toFixed(1)} months (${precisionWindowCount} elements)`);
    console.log(`  - Precision resolution: ${precisionDelta}mo per array element`);
  }
  
  // Apply milestone evidence to precision array (window-filtered only)
  let precisionItemsScored = 0;
  for (const [mvar, answerDataValue] of windowFilteredAnswers) {
    if (debug) {
      const answerLabel = input.answerSet.answers.find(a => a.dataValue === answerDataValue)?.textLabel || answerDataValue.toString();
      console.log(`  - [Pass 2] Processing milestone: ${mvar} (dataValue=${answerDataValue}, "${answerLabel}")`);
    }

    let precisionMilestoneContributed = false;

    for (let i = 0; i < precisionProbabilityArray.length; i++) {
      const currentAge = precisionWindowMin + (i * precisionDelta);

      const probValue = await interpolateProbabilityValue(
        pasData.probs,
        mvar,
        answerDataValue,
        currentAge,
        probOptions
      );

      if (probValue !== null) {
        const originalValue = precisionProbabilityArray[i];
        precisionProbabilityArray[i] *= probValue;
        precisionMilestoneContributed = true;

        if (debug && probValue !== 1.0) {
          console.log(`    - Age ${currentAge.toFixed(1)}mo: prob = ${probValue.toFixed(6)}, original = ${originalValue.toFixed(8)} → array[${i}] = ${precisionProbabilityArray[i].toFixed(8)}`);
        }
      }
    }

    if (precisionMilestoneContributed) {
      // Normalize the precision probability array to sum=100 after each Bayesian update.
      let precisionArraySum = 0;
      for (let i = 0; i < precisionProbabilityArray.length; i++) {
        precisionArraySum += precisionProbabilityArray[i];
      }

      if (precisionArraySum > 0) {
        const precisionNormFactor = 100 / precisionArraySum;
        for (let i = 0; i < precisionProbabilityArray.length; i++) {
          precisionProbabilityArray[i] *= precisionNormFactor;
        }

        if (debug) {
          console.log(`    - [Pass 2] Normalized precision array to sum=100 after applying ${mvar}`);
        }
      } else {
        console.warn(`⚠️ [Pass 2] Precision probability array sum is zero after applying ${mvar}, skipping normalization`);
      }

      precisionItemsScored++;
    }
  }
  
  // Find most probable age in precision array
  let precisionMaxProbIndex = 0;
  let precisionMaxProbValue = precisionProbabilityArray[0];
  for (let i = 1; i < precisionProbabilityArray.length; i++) {
    if (precisionProbabilityArray[i] > precisionMaxProbValue) {
      precisionMaxProbValue = precisionProbabilityArray[i];
      precisionMaxProbIndex = i;
    }
  }
  
  const developmentalAge = precisionWindowMin + (precisionMaxProbIndex * precisionDelta);
  
  if (debug) {
    console.log(`  - Refined developmental age: ${developmentalAge.toFixed(1)} months (probability: ${precisionMaxProbValue.toFixed(8)})`);
    console.log(`  - Precision improvement: ${initialDevelopmentalAge.toFixed(1)}mo → ${developmentalAge.toFixed(1)}mo`);
    computationSteps.push(`Precision refinement: ${initialDevelopmentalAge.toFixed(1)}mo → ${developmentalAge.toFixed(1)}mo using ${precisionDelta}mo resolution`);
  }

  // Step 5: Finalize developmental age result
  if (debug) {
    console.log(`  - Most probable developmental age: ${developmentalAge} months (probability: ${maxProbValue.toFixed(8)})`);
    computationSteps.push(`Most probable developmental age determined: ${developmentalAge}mo with probability ${maxProbValue.toFixed(8)}`);
  }

  // Step 6: Calculate delay metrics
  const delay = developmentalAge - input.childPhysicalAge;

  // Step 7: Compute domain-specific result variables (includes cut score analysis)
  const domainResults = await computeDomainResults(
    input.domainKey || 'GENERAL', // Use domain key if provided, otherwise default
    developmentalAge,
    input.childPhysicalAge,
    pasData.cuts,
    cutOptions
  );
  
  if (debug) {
    console.log(`  - Domain results for ${domainResults.domainKey}:`);
    console.log(`    - ${domainResults.scoreVarName}: ${domainResults.scoreValue.toFixed(1)}`);
    console.log(`    - ${domainResults.delayVarName}: ${domainResults.delayValue.toFixed(1)}`);
    console.log(`    - ${domainResults.resultVarName}: ${domainResults.resultScoreValue} (${domainResults.resultValue})`);
    console.log(`    - ${domainResults.resultScoreVarName}: ${domainResults.resultScoreValue}`);
    computationSteps.push(`Computed domain-specific variables for ${domainResults.domainKey}: RESULT=${domainResults.resultScoreValue} (${domainResults.resultValue})`);
  }

  // Convert domain result to expected format for main results
  let resultCategory: 'typical' | 'concern' | 'significant-delay' = 'typical';
  let resultScore = domainResults.resultScoreValue || 0;
  
  // Map resultValue to category
  if (domainResults.resultValue === "Above Cutoff") {
    resultCategory = 'significant-delay';
  } else if (domainResults.resultValue === "Close to Cutoff") {
    resultCategory = 'concern';
  } else {
    resultCategory = 'typical';
  }

  const result: DevelopmentalAgeResult = {
    developmentalAge: parseFloat(developmentalAge.toFixed(1)), // Always show 1 decimal place
    delay: parseFloat(delay.toFixed(1)), // Always show 1 decimal place
    result: resultCategory,
    resultScore: Math.round(resultScore * 10) / 10,
    isCappedAtDomainMax: false,
    isCappedAtDomainMin: false,
    itemsScored,
    totalItems: filteredMilestoneAnswers.size,
    rawScore: Math.round(maxProbValue * 1000) / 1000, // Use max probability as raw score
    domainResults,
    debugInfo: debug ? {
      interpolatedProbs: probScores.map(p => ({
        mvar: p.mvar,
        answerDataValue: p.answer,
        answerLabel: input.answerSet.answers.find(a => a.dataValue === p.answer)?.textLabel,
        interpolatedProb: p.probScore,
        ageUsed: p.age
      })),
      cutScoreDetails: {
        delayThresholds: [{ delay: delay, cutScore: 0 }], // Cut score details now handled in domainResults
        interpolatedCutScore: 0,
        resultCategory: domainResults.resultValue || 'Unknown'
      },
      computationSteps
    } : undefined
  };

  // if (debug) {
  //   console.log('✅ Developmental age computation completed:');
  //   console.log(`    - Developmental Age: ${result.developmentalAge}mo`);
  //   console.log(`    - Delay: ${result.delay}mo`);
  //   console.log(`    - Result: ${result.result} (score: ${result.resultScore})`);
  // }

  const elapsedTime = performance.now() - startTime;
  console.log(`⏱️ Developmental age computation completed in ${elapsedTime.toFixed(2)}ms`);

  return result;
}

/**
 * Helper function: Interpolate/extrapolate probability value from Prob table
 * 
 * This function finds ALL probability entries for a given milestone (mvar) across different ages,
 * then interpolates to find the probability for the target age and answer dataValue.
 */
export async function interpolateProbabilityValue(
  probs: AdminNodeProbDto[],
  mvar: string,
  answerDataValue: number,
  targetAge: number,
  options: ProbabilityInterpolationOptions
): Promise<number | null> {
  
  // console.log(`🔍 Interpolating probability for ${mvar} (dataValue=${answerDataValue}) at age ${targetAge}mo`);
  
  // Find ALL probability entries for this milestone across different ages
  const milestoneProbs = probs.filter(p => p.Name === mvar);
  if (milestoneProbs.length === 0) {
    console.warn(`⚠️ No probability data found for milestone ${mvar}`);
    return null;
  }
  
  // console.log(`  - Found ${milestoneProbs.length} age entries for ${mvar}`);
  
  // Create age-probability pairs by extracting data from each age entry
  const ageProbPairs: Array<{ age: number, prob: number }> = [];
  
  for (const probEntry of milestoneProbs) {
    // Parse the age from RVal 
    const age = parseFloat(probEntry.RVal);
    if (isNaN(age)) {
      console.warn(`⚠️ Invalid age in RVal: ${probEntry.RVal}`);
      continue;
    }
    
    // Find the DProb entry for our answer dataValue
    const dProbEntry = probEntry.DProbs.find(dp => {
      // Handle both camel case (TypeScript interface) and Pascal case (runtime JSON) properties
      const dVal = parseFloat(dp.dVal || (dp as any).DVal);
      return dVal === answerDataValue;
    });
    
    if (dProbEntry) {
      // Handle both camel case (TypeScript interface) and Pascal case (runtime JSON) properties
      const prob = parseFloat(dProbEntry.prob || (dProbEntry as any).Prob);
      if (!isNaN(prob)) {
        ageProbPairs.push({ age, prob });
        // console.log(`    - Age ${age}mo: prob = ${prob} (dataValue ${answerDataValue})`);
      }
    } else {
      console.log(`    - Age ${age}mo: no prob found for dataValue ${answerDataValue}`);
    }
  }
  
  if (ageProbPairs.length === 0) {
    console.warn(`⚠️ No probability values found for ${mvar} dataValue ${answerDataValue}`);
    return null;
  }
  
  // Sort by age for interpolation
  ageProbPairs.sort((a, b) => a.age - b.age);
  // console.log(`  - Age-probability graph: ${ageProbPairs.map(p => `(${p.age}, ${p.prob})`).join(', ')}`);
  
  // Perform interpolation/extrapolation to find probability at targetAge
  const interpolated = interpolateValue(ageProbPairs, targetAge, options);
  const minProbValue = Math.max(0, options.minProbValue ?? 0.001);
  return Math.max(minProbValue, interpolated);
}

/**
 * Convert numeric data value to text answer for legacy compatibility
 */
function convertDataValueToText(dataValue: number): string {
  switch (dataValue) {
    case 1: return 'yes';
    case 2: return 'sometimes';
    case 3: return 'not-yet';
    default: return dataValue.toString();
  }
}

/**
 * Standardize answer format
 */
function standardizeAnswer(answer: string): string {
  const lower = answer.toLowerCase().trim();
  switch (lower) {
    case 'yes':
    case 'y':
    case 'true':
    case '1':
      return 'yes';
    case 'sometimes':
    case 'partial':
    case 'maybe':
    case 's':
    case '0.5':
      return 'sometimes';
    case 'not-yet':
    case 'notyet':
    case 'no':
    case 'n':
    case 'false':
    case '0':
      return 'not-yet';
    default:
      return lower;
  }
}

/**
 * Get numeric value for answer (used in some PAS formats)
 */
function getNumericAnswerValue(answer: string): number {
  switch (standardizeAnswer(answer)) {
    case 'yes': return 1;
    case 'sometimes': return 0.5;
    case 'not-yet': return 0;
    default: return 0;
  }
}

/**
 * Get alternative answer formats
 */
function getAlternativeAnswerFormats(answer: string): string[] {
  switch (standardizeAnswer(answer)) {
    case 'yes':
      return ['y', 'true', '1', 'yes'];
    case 'sometimes':
      return ['s', 'partial', 'maybe', '0.5', 'sometimes'];
    case 'not-yet':
      return ['n', 'no', 'false', '0', 'notyet', 'not-yet'];
    default:
      return [answer.toLowerCase()];
  }
}

/**
 * Perform interpolation/extrapolation on probability values
 */
function interpolateValue(
  points: Array<{ age: number, prob: number }>, 
  targetAge: number, 
  options: ProbabilityInterpolationOptions
): number {
  
  if (points.length === 0) return 0;
  if (points.length === 1) return points[0].prob;
  
  // Handle exact matches
  const exactMatch = points.find(p => Math.abs(p.age - targetAge) < 0.001);
  if (exactMatch) {
    return exactMatch.prob;
  }
  
  // Sort points by age
  points.sort((a, b) => a.age - b.age);
  
  const minAge = points[0].age;
  const maxAge = points[points.length - 1].age;
  
  // Handle extrapolation cases
  if (targetAge < minAge) {
    return handleExtrapolation('below', points, targetAge, options);
  }
  
  if (targetAge > maxAge) {
    return handleExtrapolation('above', points, targetAge, options);
  }
  
  // Find bounding points for interpolation
  let lowerPoint = points[0];
  let upperPoint = points[1];
  
  for (let i = 0; i < points.length - 1; i++) {
    if (points[i].age <= targetAge && points[i + 1].age >= targetAge) {
      lowerPoint = points[i];
      upperPoint = points[i + 1];
      break;
    }
  }
  
  // Perform interpolation based on method
  switch (options.method) {
    case 'linear':
      return linearInterpolation(lowerPoint, upperPoint, targetAge);
    case 'cubic':
      return cubicInterpolation(points, targetAge);
    case 'logarithmic':
      return logarithmicInterpolation(lowerPoint, upperPoint, targetAge);
    default:
      return linearInterpolation(lowerPoint, upperPoint, targetAge);
  }
}

/**
 * Handle extrapolation beyond available age ranges
 */
function handleExtrapolation(
  direction: 'above' | 'below',
  points: Array<{ age: number, prob: number }>,
  targetAge: number,
  options: ProbabilityInterpolationOptions
): number {
  
  switch (options.extrapolationMode) {
    case 'clamp':
      return direction === 'below' ? points[0].prob : points[points.length - 1].prob;
      
    case 'extend':
      if (direction === 'below') {
        return points[0].prob;
      } else {
        return points[points.length - 1].prob;
      }
      
    case 'linear-extend':
      if (points.length < 2) {
        return points[0]?.prob || 0;
      }
      
      if (direction === 'below') {
        const p1 = points[0];
        const p2 = points[1];
        return linearExtrapolation(p1, p2, targetAge);
      } else {
        const p1 = points[points.length - 2];
        const p2 = points[points.length - 1];
        return linearExtrapolation(p1, p2, targetAge);
      }
      
    default:
      return 0;
  }
}

/**
 * Linear interpolation between two points
 */
function linearInterpolation(
  p1: { age: number, prob: number }, 
  p2: { age: number, prob: number }, 
  targetAge: number
): number {
  if (Math.abs(p2.age - p1.age) < 0.001) {
    return p1.prob; // Avoid division by zero
  }
  
  const ratio = (targetAge - p1.age) / (p2.age - p1.age);
  const interpolated = p1.prob + ratio * (p2.prob - p1.prob);
  
  // Clamp to reasonable probability range
  return Math.max(0, Math.min(1, interpolated));
}

/**
 * Linear extrapolation beyond known points
 */
function linearExtrapolation(
  p1: { age: number, prob: number }, 
  p2: { age: number, prob: number }, 
  targetAge: number
): number {
  if (Math.abs(p2.age - p1.age) < 0.001) {
    return p1.prob;
  }
  
  const slope = (p2.prob - p1.prob) / (p2.age - p1.age);
  const extrapolated = p1.prob + slope * (targetAge - p1.age);
  
  // Clamp to reasonable probability range
  return Math.max(0, Math.min(1, extrapolated));
}

/**
 * Cubic interpolation (for smoother curves with more data points)
 */
function cubicInterpolation(points: Array<{ age: number, prob: number }>, targetAge: number): number {
  // TODO: Implement cubic spline interpolation for smoother probability curves
  // For now, fall back to linear interpolation
  if (points.length < 2) return points[0]?.prob || 0;
  
  // Find the closest pair for linear interpolation as fallback
  let closestPair = [points[0], points[1]];
  let minDistance = Math.abs(points[0].age - targetAge) + Math.abs(points[1].age - targetAge);
  
  for (let i = 0; i < points.length - 1; i++) {
    const distance = Math.abs(points[i].age - targetAge) + Math.abs(points[i + 1].age - targetAge);
    if (distance < minDistance) {
      closestPair = [points[i], points[i + 1]];
      minDistance = distance;
    }
  }
  
  return linearInterpolation(closestPair[0], closestPair[1], targetAge);
}

/**
 * Logarithmic interpolation (for probability curves that follow logarithmic patterns)
 */
function logarithmicInterpolation(
  p1: { age: number, prob: number }, 
  p2: { age: number, prob: number }, 
  targetAge: number
): number {
  
  if (Math.abs(p2.age - p1.age) < 0.001) {
    return p1.prob;
  }
  
  // Ensure positive values for logarithm
  const minProb = 0.001;
  const prob1 = Math.max(minProb, p1.prob);
  const prob2 = Math.max(minProb, p2.prob);
  
  // Logarithmic interpolation
  const logRatio = (targetAge - p1.age) / (p2.age - p1.age);
  const logInterpolated = prob1 * Math.pow(prob2 / prob1, logRatio);
  
  // Clamp to reasonable probability range
  return Math.max(0, Math.min(1, logInterpolated));
}

/**
 * Helper function: Interpolate/extrapolate cut score given physical age and delay
 * 
 * This function determines the assessment result category and score based on
 * the child's delay amount and age-appropriate cut score thresholds.
 */
export async function interpolateCutScore(
  cuts: AdminNodeCutsDto[],
  physicalAge: number,
  delay: number,
  options: CutScoreInterpolationOptions
): Promise<{ 
  category: 'typical' | 'concern' | 'significant-delay', 
  score: number,
  debugInfo: any
}> {
  
  console.log(`🎯 Interpolating cut score for age ${physicalAge}mo, delay ${delay}mo`);
  
  if (!cuts || cuts.length === 0) {
    console.warn('⚠️ No cut score data available, using default thresholds');
    return applyDefaultCutScores(physicalAge, delay);
  }
  
  // Parse cut score data into age-specific thresholds
  const cutThresholds = parseCutScoreThresholds(cuts);
  console.log(`  - Parsed ${cutThresholds.length} cut score thresholds`);
  
  if (cutThresholds.length === 0) {
    return applyDefaultCutScores(physicalAge, delay);
  }
  
  // Find or interpolate thresholds for the specific age
  const ageThresholds = interpolateThresholdsForAge(cutThresholds, physicalAge, options);
  console.log(`  - Age-specific thresholds: concern=${ageThresholds.concernThreshold}mo, significant=${ageThresholds.significantThreshold}mo`);
  
  // Apply thresholds to determine category and score
  const result = applyCutScoreThresholds(delay, ageThresholds, options);
  
  console.log(`  - Result: ${result.category} (score: ${result.score})`);
  
  return {
    ...result,
    debugInfo: {
      availableThresholds: cutThresholds,
      ageSpecificThresholds: ageThresholds,
      delayAmount: delay,
      physicalAge: physicalAge,
      interpolationMethod: options.method,
      thresholdSmoothing: options.thresholdSmoothing
    }
  };
}

/**
 * Cut score threshold data structure
 */
interface CutScoreThreshold {
  age: number;                        // Age in months
  concernThreshold: number;           // Delay threshold for "concern" category
  significantThreshold: number;       // Delay threshold for "significant-delay" category
  isInterpolated?: boolean;           // Whether this threshold was interpolated
}

/**
 * Parse cut score data from AdminNodeCutsDto format
 */
function parseCutScoreThresholds(cuts: AdminNodeCutsDto[]): CutScoreThreshold[] {
  const thresholds: CutScoreThreshold[] = [];
  
  // Group cuts by age (RVal) and extract threshold values (CVal)
  const cutsByAge = new Map<number, AdminNodeCutsDto[]>();
  
  for (const cut of cuts) {
    // Parse age from RVal - could be single age like "24" or range like "18-30"
    const ages = parseAgeFromRVal(cut.RVal);
    
    for (const age of ages) {
      if (!cutsByAge.has(age)) {
        cutsByAge.set(age, []);
      }
      cutsByAge.get(age)!.push(cut);
    }
  }
  
  // Convert grouped cuts to threshold objects
  for (const [age, ageCuts] of cutsByAge) {
    const threshold = parseCutValuesForAge(age, ageCuts);
    if (threshold) {
      thresholds.push(threshold);
    }
  }
  
  // Sort by age for interpolation
  thresholds.sort((a, b) => a.age - b.age);
  
  return thresholds;
}

/**
 * Parse age values from RVal string
 */
function parseAgeFromRVal(rVal: string): number[] {
  if (!rVal) return [];
  
  const ages: number[] = [];
  
  if (rVal.includes('-')) {
    // Range format like "18-30"
    const [start, end] = rVal.split('-').map(s => parseFloat(s.trim()));
    if (!isNaN(start) && !isNaN(end)) {
      // For ranges, use the midpoint
      ages.push((start + end) / 2);
    }
  } else if (rVal.includes(',')) {
    // Multiple ages like "12,18,24"
    const ageStrings = rVal.split(',');
    for (const ageStr of ageStrings) {
      const age = parseFloat(ageStr.trim());
      if (!isNaN(age)) {
        ages.push(age);
      }
    }
  } else {
    // Single age like "24"
    const age = parseFloat(rVal.trim());
    if (!isNaN(age)) {
      ages.push(age);
    }
  }
  
  return ages;
}

/**
 * Parse cut values (CVal) for a specific age
 */
function parseCutValuesForAge(age: number, cuts: AdminNodeCutsDto[]): CutScoreThreshold | null {
  // CVal format might be like "3.0,6.0" (concern,significant) or single values
  let concernThreshold = 3.0;  // Default concern threshold (3 months delay)
  let significantThreshold = 6.0; // Default significant delay threshold (6 months delay)
  
  for (const cut of cuts) {
    const cVal = cut.CVal;
    if (!cVal) continue;
    
    if (cVal.includes(',')) {
      // Multiple thresholds like "3.0,6.0"
      const thresholds = cVal.split(',').map(s => parseFloat(s.trim()));
      if (thresholds.length >= 2 && !thresholds.some(isNaN)) {
        concernThreshold = thresholds[0];
        significantThreshold = thresholds[1];
        break;
      }
    } else {
      // Single threshold - assume it's the significant delay threshold
      const threshold = parseFloat(cVal);
      if (!isNaN(threshold)) {
        significantThreshold = threshold;
        concernThreshold = threshold / 2; // Estimate concern threshold as half
      }
    }
  }
  
  return {
    age,
    concernThreshold,
    significantThreshold
  };
}

/**
 * Interpolate thresholds for a specific age
 */
function interpolateThresholdsForAge(
  thresholds: CutScoreThreshold[], 
  targetAge: number, 
  options: CutScoreInterpolationOptions
): CutScoreThreshold {
  
  if (thresholds.length === 0) {
    return { age: targetAge, concernThreshold: 3.0, significantThreshold: 6.0 };
  }
  
  if (thresholds.length === 1) {
    return { ...thresholds[0], age: targetAge, isInterpolated: true };
  }
  
  // Find exact match
  const exactMatch = thresholds.find(t => Math.abs(t.age - targetAge) < 0.001);
  if (exactMatch) {
    return exactMatch;
  }
  
  // Find bounding thresholds
  const sortedThresholds = [...thresholds].sort((a, b) => a.age - b.age);
  
  // Handle extrapolation
  if (targetAge < sortedThresholds[0].age) {
    return handleThresholdExtrapolation('below', sortedThresholds, targetAge, options);
  }
  
  if (targetAge > sortedThresholds[sortedThresholds.length - 1].age) {
    return handleThresholdExtrapolation('above', sortedThresholds, targetAge, options);
  }
  
  // Find interpolation bounds
  let lowerThreshold = sortedThresholds[0];
  let upperThreshold = sortedThresholds[1];
  
  for (let i = 0; i < sortedThresholds.length - 1; i++) {
    if (sortedThresholds[i].age <= targetAge && sortedThresholds[i + 1].age >= targetAge) {
      lowerThreshold = sortedThresholds[i];
      upperThreshold = sortedThresholds[i + 1];
      break;
    }
  }
  
  // Perform interpolation
  return interpolateThresholdValues(lowerThreshold, upperThreshold, targetAge, options);
}

/**
 * Handle threshold extrapolation beyond available ages
 */
function handleThresholdExtrapolation(
  direction: 'above' | 'below',
  thresholds: CutScoreThreshold[],
  targetAge: number,
  options: CutScoreInterpolationOptions
): CutScoreThreshold {
  
  if (direction === 'below') {
    // For younger ages, use the first available threshold
    return { ...thresholds[0], age: targetAge, isInterpolated: true };
  } else {
    // For older ages, use the last available threshold
    return { ...thresholds[thresholds.length - 1], age: targetAge, isInterpolated: true };
  }
}

/**
 * Interpolate between two threshold points
 */
function interpolateThresholdValues(
  t1: CutScoreThreshold,
  t2: CutScoreThreshold,
  targetAge: number,
  options: CutScoreInterpolationOptions
): CutScoreThreshold {
  
  if (Math.abs(t2.age - t1.age) < 0.001) {
    return { ...t1, age: targetAge, isInterpolated: true };
  }
  
  const ratio = (targetAge - t1.age) / (t2.age - t1.age);
  
  let concernThreshold: number;
  let significantThreshold: number;
  
  switch (options.method) {
    case 'linear':
      concernThreshold = t1.concernThreshold + ratio * (t2.concernThreshold - t1.concernThreshold);
      significantThreshold = t1.significantThreshold + ratio * (t2.significantThreshold - t1.significantThreshold);
      break;
      
    case 'step':
      // Use the lower threshold until halfway, then switch to upper
      if (ratio < 0.5) {
        concernThreshold = t1.concernThreshold;
        significantThreshold = t1.significantThreshold;
      } else {
        concernThreshold = t2.concernThreshold;
        significantThreshold = t2.significantThreshold;
      }
      break;
      
    case 'smooth':
      // Smooth interpolation using cubic easing
      const smoothRatio = smoothStep(ratio);
      concernThreshold = t1.concernThreshold + smoothRatio * (t2.concernThreshold - t1.concernThreshold);
      significantThreshold = t1.significantThreshold + smoothRatio * (t2.significantThreshold - t1.significantThreshold);
      break;
      
    default:
      // Default to linear
      concernThreshold = t1.concernThreshold + ratio * (t2.concernThreshold - t1.concernThreshold);
      significantThreshold = t1.significantThreshold + ratio * (t2.significantThreshold - t1.significantThreshold);
  }
  
  return {
    age: targetAge,
    concernThreshold,
    significantThreshold,
    isInterpolated: true
  };
}

/**
 * Apply cut score thresholds to determine category and score
 */
function applyCutScoreThresholds(
  delay: number,
  thresholds: CutScoreThreshold,
  options: CutScoreInterpolationOptions
): { category: 'typical' | 'concern' | 'significant-delay', score: number } {
  
  // Apply smoothing to threshold boundaries if enabled
  let effectiveConcernThreshold = thresholds.concernThreshold;
  let effectiveSignificantThreshold = thresholds.significantThreshold;
  
  if (options.thresholdSmoothing) {
    // Add small buffer zones around thresholds to prevent boundary jumping
    const bufferSize = 0.5; // 0.5 months buffer
    effectiveConcernThreshold -= bufferSize / 2;
    effectiveSignificantThreshold -= bufferSize / 2;
  }
  
  // Determine category
  let category: 'typical' | 'concern' | 'significant-delay';
  let score: number;
  
  if (delay < effectiveConcernThreshold) {
    category = 'typical';
    score = 0;
  } else if (delay < effectiveSignificantThreshold) {
    category = 'concern';
    score = 1;
  } else {
    category = 'significant-delay';
    score = 2;
  }
  
  // For threshold smoothing, add gradual scoring near boundaries
  if (options.thresholdSmoothing) {
    const concernRange = 0.5;
    const significantRange = 0.5;
    
    // Smooth transition near concern threshold
    if (Math.abs(delay - thresholds.concernThreshold) < concernRange) {
      const ratio = (delay - (thresholds.concernThreshold - concernRange)) / (2 * concernRange);
      if (category === 'typical' && ratio > 0) {
        score = Math.min(0.5, ratio * 0.5);
      } else if (category === 'concern' && ratio < 1) {
        score = 0.5 + (ratio * 0.5);
      }
    }
    
    // Smooth transition near significant delay threshold
    if (Math.abs(delay - thresholds.significantThreshold) < significantRange) {
      const ratio = (delay - (thresholds.significantThreshold - significantRange)) / (2 * significantRange);
      if (category === 'concern' && ratio > 0.5) {
        score = 1 + ((ratio - 0.5) * 2);
      } else if (category === 'significant-delay' && ratio < 1.5) {
        score = Math.max(1.5, 2 - ((1.5 - ratio) * 0.5));
      }
    }
  }
  
  return { category, score };
}

/**
 * Apply default cut scores when no PAS data is available
 */
function applyDefaultCutScores(
  physicalAge: number, 
  delay: number
): { category: 'typical' | 'concern' | 'significant-delay', score: number, debugInfo: any } {
  
  // Age-adjusted default thresholds
  let concernThreshold = 3.0;  // 3 months for most ages
  let significantThreshold = 6.0; // 6 months for most ages
  
  // Adjust thresholds based on age
  if (physicalAge < 12) {
    // For infants, use smaller thresholds
    concernThreshold = 2.0;
    significantThreshold = 4.0;
  } else if (physicalAge > 36) {
    // For older children, use larger thresholds
    concernThreshold = 4.0;
    significantThreshold = 8.0;
  }
  
  const category = delay < concernThreshold ? 'typical' : 
                  (delay < significantThreshold ? 'concern' : 'significant-delay');
  const score = delay < concernThreshold ? 0 : (delay < significantThreshold ? 1 : 2);
  
  return {
    category,
    score,
    debugInfo: {
      usingDefaults: true,
      defaultConcernThreshold: concernThreshold,
      defaultSignificantThreshold: significantThreshold,
      delayAmount: delay,
      physicalAge: physicalAge
    }
  };
}

/**
 * Smooth step function for smooth interpolation (cubic easing)
 */
function smoothStep(t: number): number {
  // Clamp t to [0, 1]
  t = Math.max(0, Math.min(1, t));
  
  // Cubic smooth step: 3t² - 2t³
  return t * t * (3 - 2 * t);
}

/**
 * Process milestone responses from UI/storage to proper data values
 * Converts user responses to numeric data values for processing
 */
export function processMilestoneResponses(
  userResponses: Map<string, string>, // UI responses: mvar -> text answer ("Yes", "Sometimes", "Not Yet")
  answerSet: MilestoneAnswerSet
): Map<string, number> {
  
  const processedAnswers = new Map<string, number>();
  
  for (const [mvar, textAnswer] of userResponses) {
    // Find matching answer in the answer set
    const matchingAnswer = answerSet.answers.find(answer => 
      answer.textLabel.toLowerCase() === textAnswer.toLowerCase() ||
      standardizeAnswer(textAnswer) === standardizeAnswer(answer.textLabel)
    );
    
    if (matchingAnswer) {
      processedAnswers.set(mvar, matchingAnswer.dataValue);
    } else {
      console.warn(`⚠️ Could not map answer "${textAnswer}" for ${mvar}, using fallback`);
      // Fallback mapping
      const standardText = standardizeAnswer(textAnswer);
      switch (standardText) {
        case 'yes': processedAnswers.set(mvar, 1); break;
        case 'sometimes': processedAnswers.set(mvar, 2); break;
        case 'not-yet': processedAnswers.set(mvar, 3); break;
        default:
          console.warn(`⚠️ Unknown answer "${textAnswer}" for ${mvar}, skipping`);
      }
    }
  }
  
  return processedAnswers;
}

/**
 * Create developmental age input from AdminSegment and user responses
 * This is the main entry point for processing milestone data from the admin segment
 */
export function createDevelopmentalAgeInput(
  adminSegment: AdminSegmentDto,
  domainNode: AdminNodeDto,
  userResponses: Map<string, string>, // UI responses: mvar -> text answer
  childPhysicalAge: number,
  domainKey?: string,                 // Domain key for result variable naming
  languageTypeId: string = 'en',
  debugMode: boolean = false
): DevelopmentalAgeInput {
  
  // Extract answer set configuration
  const answerSet = extractAnswerSet(adminSegment, languageTypeId);
  
  // Process user responses to numeric data values
  const milestoneAnswers = processMilestoneResponses(userResponses, answerSet);
  
  return {
    domainNode,
    domainKey,
    childPhysicalAge,
    milestoneAnswers,
    answerSet,
    debugMode
  };
}

// Helper functions for data validation and processing
function validateInputData(input: DevelopmentalAgeInput): { valid: boolean, errors: string[] } {
  const errors: string[] = [];
  
  if (!input.domainNode) {
    errors.push('Domain node is required');
  }
  
  if (!input.childPhysicalAge || input.childPhysicalAge <= 0) {
    errors.push('Valid child physical age is required');
  }
  
  if (!input.milestoneAnswers || input.milestoneAnswers.size === 0) {
    errors.push('Milestone answers are required');
  }
  
  if (!input.answerSet || !input.answerSet.answers || input.answerSet.answers.length === 0) {
    errors.push('Valid answer set configuration is required');
  }
  
  // Validate that all milestone answers use valid data values
  if (input.answerSet && input.milestoneAnswers) {
    const validDataValues = new Set(input.answerSet.answers.map(a => a.dataValue));
    for (const [mvar, dataValue] of input.milestoneAnswers) {
      if (!validDataValues.has(dataValue)) {
        errors.push(`Invalid data value ${dataValue} for milestone ${mvar}`);
      }
    }
  }
  
  return { valid: errors.length === 0, errors };
}

function extractPasData(domainNode: AdminNodeDto) {
  return {
    order: domainNode.Order || [],
    params: domainNode.Params || [],
    means: domainNode.Means || [],
    probs: domainNode.Probs || [],
    cuts: domainNode.Cuts || []
  };
}

function convertRawScoreToAge(rawScore: number, means: AdminNodeMeanDto[], referenceAge: number): number {
  console.log(`🔄 Converting raw score ${rawScore.toFixed(4)} to developmental age`);
  
  if (!means || means.length === 0) {
    console.warn('⚠️ No means data available, using simplified score-to-age conversion');
    // Fallback: assume linear relationship where score of 1.0 = reference age
    return referenceAge * rawScore;
  }
  
  // Parse means data to find age-score relationships
  const ageScorePairs: Array<{ age: number, meanScore: number }> = [];
  
  for (const meanEntry of means) {
    // Parse age from Name (could be like "age_24" or just "24")
    const age = parseAgeFromMeanName(meanEntry.Name);
    if (age !== null) {
      const meanScore = parseFloat(meanEntry.Mean);
      if (!isNaN(meanScore)) {
        ageScorePairs.push({ age, meanScore });
        console.log(`    - Age ${age}mo: mean score = ${meanScore.toFixed(4)}`);
      }
    }
  }
  
  if (ageScorePairs.length === 0) {
    console.warn('⚠️ Could not parse means data, using fallback conversion');
    return referenceAge * rawScore;
  }
  
  // Sort by mean score for interpolation
  ageScorePairs.sort((a, b) => a.meanScore - b.meanScore);
  
  // Handle exact score matches
  const exactMatch = ageScorePairs.find(pair => Math.abs(pair.meanScore - rawScore) < 0.0001);
  if (exactMatch) {
    console.log(`    ✅ Exact score match found: ${rawScore} → ${exactMatch.age}mo`);
    return exactMatch.age;
  }
  
  // Handle scores outside the available range
  if (rawScore <= ageScorePairs[0].meanScore) {
    const developmentalAge = ageScorePairs[0].age;
    console.log(`    📉 Score below minimum, using lowest age: ${developmentalAge}mo`);
    return developmentalAge;
  }
  
  if (rawScore >= ageScorePairs[ageScorePairs.length - 1].meanScore) {
    const developmentalAge = ageScorePairs[ageScorePairs.length - 1].age;
    console.log(`    📈 Score above maximum, using highest age: ${developmentalAge}mo`);
    return developmentalAge;
  }
  
  // Find bounding pairs for interpolation
  let lowerPair = ageScorePairs[0];
  let upperPair = ageScorePairs[1];
  
  for (let i = 0; i < ageScorePairs.length - 1; i++) {
    if (ageScorePairs[i].meanScore <= rawScore && ageScorePairs[i + 1].meanScore >= rawScore) {
      lowerPair = ageScorePairs[i];
      upperPair = ageScorePairs[i + 1];
      break;
    }
  }
  
  // Linear interpolation between ages based on scores
  const scoreRange = upperPair.meanScore - lowerPair.meanScore;
  if (Math.abs(scoreRange) < 0.0001) {
    console.log(`    ⚖️ Equal scores, using average age: ${(lowerPair.age + upperPair.age) / 2}mo`);
    return (lowerPair.age + upperPair.age) / 2;
  }
  
  const scoreRatio = (rawScore - lowerPair.meanScore) / scoreRange;
  const developmentalAge = lowerPair.age + scoreRatio * (upperPair.age - lowerPair.age);
  
  console.log(`    🎯 Interpolated: score ${rawScore.toFixed(4)} → ${developmentalAge.toFixed(1)}mo`);
  console.log(`      (between ${lowerPair.meanScore.toFixed(4)}@${lowerPair.age}mo and ${upperPair.meanScore.toFixed(4)}@${upperPair.age}mo)`);
  
  return developmentalAge;
}

/**
 * Parse age from mean name (e.g., "age_24" → 24, "24" → 24)
 */
function parseAgeFromMeanName(name: string): number | null {
  if (!name) return null;
  
  // Try different patterns
  const patterns = [
    /age[_-]?(\d+)/i,  // "age_24", "age-24", "age24"
    /^(\d+)$/,         // "24"
    /month[_-]?(\d+)/i, // "month_24", "month-24"
    /mo[_-]?(\d+)/i,   // "mo_24", "mo-24"
  ];
  
  for (const pattern of patterns) {
    const match = name.match(pattern);
    if (match) {
      const age = parseInt(match[1]);
      if (!isNaN(age) && age > 0 && age <= 120) { // Reasonable age range
        return age;
      }
    }
  }
  
  return null;
}

function calculateConfidence(scores: any[], itemsScored: number, totalItems: number): number {
  console.log(`🎯 Calculating confidence: ${itemsScored} scored out of ${totalItems} total`);
  
  if (itemsScored === 0) {
    return 0;
  }
  
  // Base confidence on coverage
  const coverage = itemsScored / totalItems;
  let confidence = coverage;
  
  // Adjust confidence based on number of items scored
  if (itemsScored < 3) {
    // Very low confidence with few items
    confidence *= 0.5;
  } else if (itemsScored < 5) {
    // Moderate penalty for low item count
    confidence *= 0.8;
  }
  
  // Bonus for good coverage
  if (coverage > 0.8) {
    confidence = Math.min(1.0, confidence * 1.1);
  }
  
  // Calculate score consistency (standard deviation penalty)
  if (scores.length > 1) {
    const probValues = scores.map(s => s.probScore);
    const mean = probValues.reduce((sum, val) => sum + val, 0) / probValues.length;
    const variance = probValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / probValues.length;
    const stdDev = Math.sqrt(variance);
    
    // Penalize high variability (inconsistent scores)
    const consistencyPenalty = Math.min(0.3, stdDev * 0.5);
    confidence = Math.max(0.1, confidence - consistencyPenalty);
    
    console.log(`    - Score consistency: mean=${mean.toFixed(3)}, stdDev=${stdDev.toFixed(3)}, penalty=${consistencyPenalty.toFixed(3)}`);
  }
  
  console.log(`    ✅ Final confidence: ${(confidence * 100).toFixed(1)}%`);
  
  return Math.round(confidence * 100) / 100; // Round to 2 decimals
}

// Default configuration options
export function getDefaultProbabilityOptions(): ProbabilityInterpolationOptions {
  return {
    method: 'linear',
    extrapolationMode: 'linear-extend',
    minAge: 0,
    maxAge: 120, // 10 years
    minProbValue: 0.001,
  };
}

export function getDefaultCutScoreOptions(): CutScoreInterpolationOptions {
  return {
    method: 'linear',
    thresholdSmoothing: true
  };
}

export function getDefaultAPrioriOptions(): APrioriOptions {
  return {
    pass1MaxBoost: 10.0,
    pass1StdDevFactor: 0.2,
    pass2MaxBoost: 1.0,
    pass2StdDev: 0.3
  };
}

// Utility functions for other components
export function formatDevelopmentalAge(ageInMonths: number): string {
  const years = Math.floor(ageInMonths / 12);
  const months = Math.round(ageInMonths % 12);
  
  if (years === 0) {
    return `${months} month${months !== 1 ? 's' : ''}`;
  } else if (months === 0) {
    return `${years} year${years !== 1 ? 's' : ''}`;
  } else {
    return `${years} year${years !== 1 ? 's' : ''}, ${months} month${months !== 1 ? 's' : ''}`;
  }
}

export function formatDelay(delayInMonths: number): string {
  if (delayInMonths <= 0) {
    return 'No delay';
  }
  
  return `${delayInMonths.toFixed(1)} month${delayInMonths !== 1 ? 's' : ''} delay`;
}

export function getResultDescription(result: DevelopmentalAgeResult): string {
  switch (result.result) {
    case 'typical':
      return 'Development appears typical for age';
    case 'concern':
      return 'Some developmental concerns identified';
    case 'significant-delay':
      return 'Significant developmental delay identified';
    default:
      return 'Assessment result unavailable';
  }
}

/**
 * Convenience function to compute developmental age for a domain using PAS data from storage
 * 
 * This function integrates with the existing AdminSegmentStorage to fetch PAS data
 * and compute developmental age for a specific domain and child profile.
 */
export async function computeDevelopmentalAgeForDomain(
  domainKey: string,
  childPhysicalAge: number,
  milestoneAnswers: Map<string, string>,
  adminSegmentStorage: any, // AdminSegmentStorage instance
  contentService: any,
  languageId: string = '1',
  milestoneType: 'standard' | 'premium' = 'standard',
  debugMode: boolean = false
): Promise<DevelopmentalAgeResult> {
  
  console.log(`🏗️ Computing developmental age for ${domainKey} domain`);
  console.log(`  - Child age: ${childPhysicalAge} months`);
  console.log(`  - Milestone type: ${milestoneType}`);
  console.log(`  - Answers provided: ${milestoneAnswers.size}`);
  
  // Get the appropriate AdminSegment based on milestone type
  const mapping = (adminSegmentStorage as any).MILESTONE_NODE_MAPPING[milestoneType];
  if (!mapping) {
    throw new Error(`No milestone mapping found for type: ${milestoneType}`);
  }
  
  const segmentId = mapping.segmentId;
  const nodeId = mapping.nodes[domainKey];
  
  if (!nodeId) {
    throw new Error(`No node mapping found for domain: ${domainKey} in ${milestoneType} mode`);
  }
  
  console.log(`  - Using AdminSegment ${segmentId}, node ${nodeId}`);
  
  // Load AdminSegment data
  const adminSegment = await adminSegmentStorage.getAdminSegment(segmentId, languageId, contentService);
  if (!adminSegment) {
    throw new Error(`AdminSegment ${segmentId} not found`);
  }
  
  // Find the domain node
  const domainNode = adminSegment.Nodes?.find((n: any) => n.Name === nodeId);
  if (!domainNode) {
    throw new Error(`Domain node ${nodeId} not found in AdminSegment ${segmentId}`);
  }
  
  console.log(`  ✅ Found domain node with ${domainNode.Probs?.length || 0} probs, ${domainNode.Means?.length || 0} means, ${domainNode.Cuts?.length || 0} cuts`);
  
  // Extract answer set and process milestone answers to numeric data values
  const answerSet = extractAnswerSet(adminSegment, languageId === '1' ? 'en' : 'es'); // Map language ID to language code
  const processedMilestoneAnswers = processMilestoneResponses(milestoneAnswers, answerSet);

  console.log(`🧮 Computing developmental age with ${processedMilestoneAnswers.size} user-entered milestone answers`);
  
  // Create developmental age input
  const input: DevelopmentalAgeInput = {
    domainNode,
    domainKey,
    milestoneAnswers: processedMilestoneAnswers,
    childPhysicalAge,
    answerSet,
    debugMode
  };

  // Compute the developmental age
  return await computeDevelopmentalAge(input);
}

/**
 * Optimized function to compute developmental age using cached domain data
 * This avoids unpacking AdminSegment data every time
 */
export async function computeDevelopmentalAgeWithCachedData(
  domainKey: string,
  childPhysicalAge: number,
  milestoneAnswers: Map<string, number>, // Already processed numeric values
  adminSegmentStorage: any, // AdminSegmentStorage instance
  contentService: any,
  languageId: string = '1',
  milestoneType: 'standard' | 'premium' = 'standard',
  debugMode: boolean = false
): Promise<DevelopmentalAgeResult> {
  
  console.log(`🚀 Computing developmental age for ${domainKey} domain (OPTIMIZED with cached data)`);
  console.log(`  - Child age: ${childPhysicalAge} months`);
  console.log(`  - Milestone type: ${milestoneType}`);
  console.log(`  - Answers provided: ${milestoneAnswers.size}`);
  
  // Get the appropriate AdminSegment mapping
  const mapping = (adminSegmentStorage as any).MILESTONE_NODE_MAPPING[milestoneType];
  if (!mapping) {
    throw new Error(`No milestone mapping found for type: ${milestoneType}`);
  }
  
  const segmentId = mapping.segmentId;
  
  // Get cached domain data (will cache if not available)
  const cachedData = await adminSegmentStorage.getCachedDomainData(
    domainKey, 
    segmentId, 
    languageId, 
    contentService
  );
  
  if (!cachedData) {
    throw new Error(`Failed to get cached domain data for ${domainKey}`);
  }
  
  console.log(`  ✅ Using cached domain data with ${cachedData.milestones.length} milestones`);
  console.log(`  - AdminNode has: ${cachedData.adminNode.Probs?.length || 0} probs, ${cachedData.adminNode.Means?.length || 0} means, ${cachedData.adminNode.Cuts?.length || 0} cuts`);
  
  // Create developmental age input with cached data
  const input: DevelopmentalAgeInput = {
    domainNode: cachedData.adminNode,
    domainKey,
    milestoneAnswers,
    childPhysicalAge,
    answerSet: {
      languageTypeId: languageId === '1' ? 'en' : 'es',
      answers: [
        { dataValue: 1, textLabel: 'Yes', keyValue: 1 },
        { dataValue: 2, textLabel: 'Sometimes', keyValue: 2 },
        { dataValue: 3, textLabel: 'Not Yet', keyValue: 3 }
      ]
    },
    debugMode
  };

  console.log(`🧮 Computing developmental age with ${milestoneAnswers.size} user-entered milestone answers`);
  
  // Compute the developmental age
  return await computeDevelopmentalAge(input);
}

/**
 * Optimized function to compute developmental age using a local AdminSegment
 * This completely avoids backend calls by working directly with the unpacked AdminSegment
 */
export async function computeDevelopmentalAgeWithLocalAdminSegment(
  domainKey: string,
  childPhysicalAge: number,
  milestoneAnswers: Map<string, number>, // Already processed numeric values
  adminSegment: any, // Already unpacked AdminSegmentDto
  languageId: string = '1',
  milestoneType: 'standard' | 'premium' = 'standard',
  debugMode: boolean = false
): Promise<DevelopmentalAgeResult> {
  
  console.log(`🚀 Computing developmental age for ${domainKey} domain (LOCAL AdminSegment - no backend calls)`);
  console.log(`  - Child age: ${childPhysicalAge} months`);
  console.log(`  - Milestone type: ${milestoneType}`);
  console.log(`  - Answers provided: ${milestoneAnswers.size}`);
  
  // Define milestone mapping inline to avoid dynamic import
  const MILESTONE_NODE_MAPPING = {
    standard: {
      segmentId: '11255',
      nodes: {
        communication: 'CDC_START',
        motor: 'CDC_MT',
        cognitive: 'CDC_CG',
        socialEmotional: 'CDC_SE'
      }
    },
    premium: {
      segmentId: '11270',
      nodes: {
        communication: 'CDC_START',
        grossMotor: 'CDC_MT',
        fineMotor: 'CDC_FM',
        cognitive: 'CDC_CG',
        socialEmotional: 'CDC_SE'
      }
    }
  };
  
  // Get the appropriate AdminSegment mapping
  const mapping = MILESTONE_NODE_MAPPING[milestoneType];
  if (!mapping) {
    throw new Error(`No milestone mapping found for type: ${milestoneType}`);
  }
  
  // Type-safe node lookup
  const nodeId = (mapping.nodes as any)[domainKey];
  if (!nodeId) {
    throw new Error(`No node mapping found for domain: ${domainKey} in ${milestoneType} mode`);
  }
  
  // Find the domain node directly in the AdminSegment
  const adminNode = adminSegment.Nodes?.find((n: any) => n.Name === nodeId);
  if (!adminNode) {
    throw new Error(`Domain node ${nodeId} not found in AdminSegment`);
  }
  
  console.log(`  ✅ Using domain node ${nodeId} directly from AdminSegment`);
  console.log(`  - AdminNode has: ${adminNode.Probs?.length || 0} probs, ${adminNode.Means?.length || 0} means, ${adminNode.Cuts?.length || 0} cuts`);
  
  // Create developmental age input with local AdminNode data
  const input: DevelopmentalAgeInput = {
    domainNode: adminNode,
    domainKey,
    milestoneAnswers,
    childPhysicalAge,
    answerSet: {
      languageTypeId: languageId === '1' ? 'en' : 'es',
      answers: [
        { dataValue: 1, textLabel: 'Yes', keyValue: 1 },
        { dataValue: 2, textLabel: 'Sometimes', keyValue: 2 },
        { dataValue: 3, textLabel: 'Not Yet', keyValue: 3 }
      ]
    },
    debugMode
  };

  console.log(`🧮 Computing developmental age with ${milestoneAnswers.size} user-entered milestone answers`);
  
  // Compute the developmental age
  return await computeDevelopmentalAge(input);
}

/**
 * Optimized developmental age computation that checks stored AdminNodeDto first
 * Falls back to AdminSegment parsing only if AdminNodeDto not found
 * 
 * This is the recommended function for production use as it prioritizes cached data
 */
export async function computeDevelopmentalAgeOptimized(
  domainKey: string,
  childPhysicalAge: number,
  milestoneAnswers: Map<string, string>, // UI responses: mvar -> text answer
  adminSegmentStorage: any, // AdminSegmentStorage instance
  contentService: any,
  languageId: string = '1',
  milestoneType: 'standard' | 'premium' = 'standard',
  debugMode: boolean = false
): Promise<DevelopmentalAgeResult> {
  
  console.log(`🚀 OPTIMIZED: Computing developmental age for ${domainKey} domain`);
  console.log(`  - Child age: ${childPhysicalAge} months`);
  console.log(`  - Milestone type: ${milestoneType}`);
  console.log(`  - Answers provided: ${milestoneAnswers.size}`);

  // Step 1: Try to get AdminNodeDto from stored data first (fastest path)
  try {
    console.log(`📋 Step 1: Checking for stored AdminNodeDto data...`);
    
    // Map domain key to AdminNode name
    const isPremium = milestoneType === 'premium';
    const adminNodeName = mapDomainToAdminNodeName(domainKey, isPremium);
    
    console.log(`  - Domain: ${domainKey} → AdminNode: ${adminNodeName}`);
    
    // Try to get stored AdminNodeDto
    const adminNodeDto = await adminSegmentStorage.getAdminNodeByDomain(adminNodeName);
    
    if (adminNodeDto && adminNodeDto.Probs && adminNodeDto.Means && adminNodeDto.Cuts) {
      console.log(`  ✅ Found stored AdminNodeDto with ${adminNodeDto.Probs?.length || 0} probs, ${adminNodeDto.Means?.length || 0} means, ${adminNodeDto.Cuts?.length || 0} cuts`);
      
      // Process milestone answers to numeric values (we need answer set for this)
      const segmentId = isPremium ? '11270' : '11255';
      
      // Get AdminSegment for answer set only (minimal loading)
      const adminSegment = await adminSegmentStorage.getAdminSegment(segmentId, languageId, contentService);
      if (!adminSegment) {
        throw new Error(`AdminSegment ${segmentId} not found for answer set`);
      }
      
      const answerSet = extractAnswerSet(adminSegment, languageId === '1' ? 'en' : 'es');
      const processedMilestoneAnswers = processMilestoneResponses(milestoneAnswers, answerSet);
      
      // Create input with stored AdminNodeDto
      const input: DevelopmentalAgeInput = {
        domainNode: adminNodeDto,
        domainKey,
        milestoneAnswers: processedMilestoneAnswers,
        childPhysicalAge,
        answerSet,
        debugMode
      };
      
      console.log(`🎯 Using stored AdminNodeDto data (fast path)`);
      return await computeDevelopmentalAge(input);
    } else {
      console.log(`  ⚠️ Stored AdminNodeDto incomplete or missing for ${adminNodeName}`);
    }
  } catch (error) {
    console.log(`  ⚠️ Error accessing stored AdminNodeDto:`, error);
  }

  // Step 2: Fallback to full AdminSegment parsing (slower but reliable)
  console.log(`📦 Step 2: Falling back to AdminSegment parsing...`);
  return await computeDevelopmentalAgeForDomain(
    domainKey,
    childPhysicalAge,
    milestoneAnswers,
    adminSegmentStorage,
    contentService,
    languageId,
    milestoneType,
    debugMode
  );
}

/**
 * Batch compute developmental ages for multiple domains
 */
export async function computeDevelopmentalAgesForAllDomains(
  domains: string[],
  childPhysicalAge: number,
  allMilestoneAnswers: Map<string, string>, // All answers across all domains
  adminSegmentStorage: any,
  contentService: any,
  languageId: string = '1',
  milestoneType: 'standard' | 'premium' = 'standard',
  debugMode: boolean = false
): Promise<Map<string, DevelopmentalAgeResult>> {
  
  console.log(`🏭 Batch computing developmental ages for ${domains.length} domains`);
  
  const results = new Map<string, DevelopmentalAgeResult>();
  
  for (const domain of domains) {
    try {
      console.log(`\n📊 Processing domain: ${domain}`);
      
      // Filter milestone answers to only include those relevant to this domain
      // This requires getting the domain order list to know which milestones belong to this domain
      const domainMilestoneAnswers = await filterAnswersForDomain(
        domain, 
        allMilestoneAnswers, 
        adminSegmentStorage, 
        contentService, 
        languageId, 
        milestoneType
      );
      
      console.log(`  - ${domainMilestoneAnswers.size} relevant answers for ${domain}`);
      
      if (domainMilestoneAnswers.size === 0) {
        console.warn(`  ⚠️ No answers found for domain ${domain}, skipping`);
        continue;
      }
      
      // Compute developmental age for this domain
      const result = await computeDevelopmentalAgeForDomain(
        domain,
        childPhysicalAge,
        domainMilestoneAnswers,
        adminSegmentStorage,
        contentService,
        languageId,
        milestoneType,
        debugMode
      );
      
      results.set(domain, result);
      console.log(`  ✅ ${domain}: ${result.developmentalAge}mo (${result.result})`);
      
    } catch (error) {
      console.error(`  ❌ Error processing domain ${domain}:`, error);
      // Continue with other domains even if one fails
    }
  }
  
  console.log(`\n🎯 Batch computation completed: ${results.size}/${domains.length} domains processed`);
  
  return results;
}

/**
 * Filter milestone answers to only include those relevant to a specific domain
 */
async function filterAnswersForDomain(
  domainKey: string,
  allAnswers: Map<string, string>,
  adminSegmentStorage: any,
  contentService: any,
  languageId: string,
  milestoneType: 'standard' | 'premium'
): Promise<Map<string, string>> {
  
  // Get domain order list to know which milestones belong to this domain
  try {
    const mapping = (adminSegmentStorage as any).MILESTONE_NODE_MAPPING[milestoneType];
    const segmentId = mapping.segmentId;
    const nodeId = mapping.nodes[domainKey];
    
    const adminSegment = await adminSegmentStorage.getAdminSegment(segmentId, languageId, contentService);
    if (!adminSegment) {
      return new Map();
    }
    
    const domainNode = adminSegment.Nodes?.find((n: any) => n.Name === nodeId);
    if (!domainNode || !domainNode.Order) {
      return new Map();
    }
    
    // Extract mvar names from the order list
    const domainMvars = new Set<string>();
    for (const orderItem of domainNode.Order) {
      if (orderItem.Name) {
        domainMvars.add(orderItem.Name);
      }
    }
    
    // Filter answers to only include those for this domain
    const filteredAnswers = new Map<string, string>();
    for (const [mvar, answer] of allAnswers) {
      if (domainMvars.has(mvar)) {
        filteredAnswers.set(mvar, answer);
      }
    }
    
    return filteredAnswers;
    
  } catch (error) {
    console.error(`Error filtering answers for domain ${domainKey}:`, error);
    return new Map();
  }
}